import {useState, useEffect, useMemo} from 'react';
import {graphql, forecastFetchQuery} from '../../../graphql/forecastRelay';
import type {useProjectSearchQuery$data} from './__generated__/useProjectSearchQuery.graphql';

// Uncapped on purpose: viewer.projects ignores `searchQuery` and service-company
// returns every project anyway, so a `first:` cap would only hide projects.
const ProjectSearchQueryDoc = graphql`
	query useProjectSearchQuery {
		viewer {
			projects(excludeDoneOrHalted: true) {
				edges {
					node {
						id
						name
						color: projectColor
					}
				}
			}
		}
	}
`;

export interface ProjectSearchResult {
	id: string;
	name: string;
	color: string | null;
}

/**
 * Loads the viewer's active projects once each time `enabled` turns on, then
 * filters them by name locally, so typing never triggers a refetch.
 */
export function useProjectSearch(query: string, enabled = true) {
	const [projects, setProjects] = useState<ProjectSearchResult[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!enabled) return;

		let cancelled = false;
		setLoading(true);

		forecastFetchQuery(ProjectSearchQueryDoc, {})
			.then(data => {
				if (cancelled) return;
				const typed = data as useProjectSearchQuery$data | null;
				setProjects(
					(typed?.viewer?.projects?.edges ?? []).flatMap(edge =>
						edge?.node && edge.node.name
							? [{id: edge.node.id, name: edge.node.name, color: edge.node.color ?? null}]
							: []
					)
				);
			})
			.catch(() => {
				if (!cancelled) setProjects([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [enabled]);

	const needle = query.trim().toLowerCase();

	const results = useMemo(
		() => (enabled && needle ? projects.filter(project => project.name.toLowerCase().includes(needle)) : []),
		[enabled, needle, projects]
	);

	return {results, loading: enabled && needle.length > 0 && loading};
}
